import { useCallback, useRef } from 'react'

import ProductOptionValue from '@src/quince-site-common/src/domains/ProductOption'
import { partition } from '@src/utils/functions/utils'

interface MiniPdpOptionListState {
  sourceKey: string
  collapsed: ProductOptionValue[]
  expanded?: ProductOptionValue[]
}

interface MiniPdpOptionListParams {
  validOptionValueList: ProductOptionValue[]
  selectedValue: string | undefined
  optionLimit: number
  showMoreOption?: boolean
}

export interface MiniPdpOptionListResult {
  filteredOptionValueList: ProductOptionValue[]
  noOfExcessElements: number
}

// Keyed by limit too: the mini PDP derives optionLimit from the measured row width,
// so a frozen order built for a stale limit must be rebuilt when the width changes.
const getOptionValuesKey = (options: ProductOptionValue[], optionLimit: number) =>
  [optionLimit, ...options.map(option => option.value)].join('|')

const buildCollapsedList = (
  validOptionValueList: ProductOptionValue[],
  selectedValue: string | undefined,
  optionLimit: number,
): ProductOptionValue[] => {
  const [selectedOptionArray, unSelectedOptionArray] = partition(
    validOptionValueList,
    optionValue => optionValue.value === selectedValue,
  )

  return [...selectedOptionArray, ...unSelectedOptionArray.slice(0, optionLimit - selectedOptionArray.length)]
}

const buildExpandedList = (
  collapsedList: ProductOptionValue[],
  validOptionValueList: ProductOptionValue[],
): ProductOptionValue[] => {
  const visibleValues = new Set(collapsedList.map(option => option.value))

  return [...collapsedList, ...validOptionValueList.filter(option => !visibleValues.has(option.value))]
}

/** Mini PDP only: inline "+1", freeze initial collapsed order, append hidden colors on expand. */
export const useMiniPdpOptionList = () => {
  const orderRef = useRef<MiniPdpOptionListState | null>(null)

  const getMiniPdpOptionList = useCallback(
    ({
      validOptionValueList,
      selectedValue,
      optionLimit,
      showMoreOption,
    }: MiniPdpOptionListParams): MiniPdpOptionListResult => {
      const rawExcess = Math.max(validOptionValueList.length - optionLimit, 0)

      if (rawExcess <= 1) {
        return { filteredOptionValueList: validOptionValueList, noOfExcessElements: 0 }
      }

      const sourceKey = getOptionValuesKey(validOptionValueList, optionLimit)

      if (orderRef.current?.sourceKey !== sourceKey) {
        orderRef.current = {
          sourceKey,
          collapsed: buildCollapsedList(validOptionValueList, selectedValue, optionLimit),
        }
      }

      const { collapsed } = orderRef.current

      if (!showMoreOption) {
        if (!orderRef.current.expanded) {
          orderRef.current.expanded = buildExpandedList(collapsed, validOptionValueList)
        }

        return { filteredOptionValueList: orderRef.current.expanded, noOfExcessElements: 0 }
      }

      return { filteredOptionValueList: collapsed, noOfExcessElements: rawExcess }
    },
    [],
  )

  return getMiniPdpOptionList
}
